import { QueryManager } from '@codeleap/query'
import { TypeGuards } from '@codeleap/types'
import { getChatClient } from '@packages/chat'
import { MessageReaction, ChatLocalMesage } from '@packages/chat'
import { queryClient } from '../queryClient'
import { GetStreamConfig } from '@/config'

type ListReactionFilter = {
  messageId: ChatLocalMesage['id']
}

type MessageReactionWithId = MessageReaction & { id: string }

export const reactionsManager = new QueryManager<MessageReactionWithId>({
  itemType: {} as MessageReactionWithId,
  name: 'reactions',
  queryClient: queryClient.client,
  limit: 20,

  listItems: async (limit = 20, offset, filters: ListReactionFilter) => {
    const client = await getChatClient(GetStreamConfig.values.chatApiKey)
    const messageId = filters?.messageId

    const response = await client.queryReactions(
      messageId,
      {},
      {},
      { limit, next: TypeGuards.isString(offset) ? offset : undefined },
    )

    const results = response.reactions.map((reaction, index) => {
      const userId = reaction.user?.id || reaction.user_id || 'unknown'
      const type = reaction.type || ''
      const createdAt = reaction.created_at || ''
      const uniqueId = `${messageId}-${userId}-${type}-${createdAt}-${index}`

      return {
        ...reaction,
        id: uniqueId,
      }
    })

    return {
      count: results.length + limit,
      next: response.next,
      results,
      previous: undefined,
    }
  },
})

export function useReactions({ messageId }: { messageId: ChatLocalMesage['id'] }) {
  const list = reactionsManager.useList({
    filter: { messageId },
    queryOptions: {
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      getNextPageParam(lastPage) {
        if (!lastPage?.next) return undefined

        return {
          limit: 20,
          offset: lastPage.next,
        } as any
      },
    },
  })

  return list
}

export function invalidateReactionsCache(messageId: ChatLocalMesage['id']) {
  reactionsManager.queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey
      return Array.isArray(key) &&
             key[0] === 'reactions' &&
             key[1]?.messageId === messageId
    },
  })
}
